import { useContext, useMemo, useState, useRef, useEffect } from "react"

import { GatsbyImage, IGatsbyImageData } from "gatsby-plugin-image"
import { useI18next } from "gatsby-plugin-react-i18next"
import gsap from "gsap"
import styled, { css } from "styled-components"

import LinkButton from "components/buttons/LinkButton"
import InnerWrapper from "components/InnerWrapper"
import { ScreenContext } from "components/Providers"
import Callout from "components/TextElements/Callout"
import colors from "styles/colors"
import media from "styles/media"
import text from "styles/text"
import { fmobile, fresponsive, ftablet } from "utils/fullyResponsive"
import { checkedLocaleLink } from "utils/routes"
import useMedia from "utils/useMedia"

interface Sections {
  body: string
  link: {
    text: string
    url: string
  }
  img: IGatsbyImageData | undefined
  alt?: string
}

interface ButtonsArr {
  buttonText: string
}

interface Gradient {
  gradient: string
  fill: string
}

interface Props {
  gradient: Gradient
  sections: Sections[]
  buttonsArr: ButtonsArr[]
  calloutText?: { title: string; text: string }
  className?: string
}

export default function Carousel({
  sections,
  buttonsArr,
  gradient,
  calloutText = { title: "", text: "" },
  className = "",
}: Props) {
  const { t, language } = useI18next()

  const { tablet } = useContext(ScreenContext)
  const titleWidth = useMedia("415px", "25vw", "45.898vw", "")
  const textWidth = useMedia("415px", "25vw", "34.082vw", "")
  const [autoPlay, setAutoPlay] = useState(true)
  const [nextIndex, setNextIndex] = useState(0)
  const timer = useRef(5)

  type Language = "pt" | "es" | "en"

  const titles: Record<Language, string> = {
    pt: "Uma plataforma, experiências personalizadas.",
    es: "Una plataforma, experiencias personalizadas.",
    en: "One platform, tailored experiences.",
  }

  const descriptions: Record<Language, string> = {
    pt: "Seja o seu negócio retalho, PME, corporativo ou pagamentos, a Cyberbank pode ajudá-lo a iniciar a sua transformação digital bancária ou projeto de startup em tão pouco quanto três meses.",
    es: "Ya sea que tu negocio sea minorista, PYME, corporativo o de pagos, Cyberbank puede ayudarte a iniciar tu transformación bancaria digital o proyecto de startup en tan solo tres meses.",
    en: "Whether your business is retail, SME, corporate or payments, Cyberbank can help you kick-start your digital banking transformation or startup project in as little as three months.",
  }

  const titleMain = titles[language as Language] || titles.en
  const descMain = descriptions[language as Language] || descriptions.en

  const handleClick = (index: number) => {
    setAutoPlay(false)
    setNextIndex(index)
    timer.current = 10000
  }

  useEffect(() => {
    const tl = gsap.timeline({
      onComplete: () => {
        if (!autoPlay) {
          return
        }
        let index = nextIndex + 1
        if (index >= sections.length) {
          index = 0
        }
        setNextIndex(index)
      },
      scrollTrigger: {
        trigger: `#button_0`,
        start: "bottom 150%",
        toggleActions: "play pause play pause",
      },
    })

    tl.to({}, { duration: 5 })
      .to(
        `.carousel-div`,
        {
          duration: 0.3,
          opacity: 0,
        },
        0
      )

      .to(
        `.carousel-button`,
        {
          duration: 0.3,
          backgroundColor: `${colors.blue10}`,
          color: `${colors.blue800}`,
        },
        0
      )

    tl.to(
      `#rotatingDiv_${nextIndex}`,
      {
        duration: 0.3,
        opacity: 1,
      },
      0.2
    )

    tl.to(
      `#button_${nextIndex}`,
      {
        duration: 0.3,
        backgroundColor: `${colors.blue400}`,
        color: "white",
      },
      0.2
    )

    return () => {
      tl.kill()
    }
  }, [nextIndex, sections, autoPlay])

  const allButtons = useMemo(() => {
    return buttonsArr.map((buttonData, index) => {
      return (
        <Buttons
          key={`button_${buttonData.buttonText}`}
          className="carousel-button"
          id={`button_${index}`}
          onClick={() => handleClick(index)}
          onKeyUp={() => handleClick(index)}
          role="presentation"
        >
          {buttonData.buttonText}
        </Buttons>
      )
    })
  }, [buttonsArr])

  const rotatingContent = useMemo(() => {
    return sections.map((contentData, index) => {
      return (
        <RotationDiv
          key={`rotatingDiv_${contentData.link.text}`}
          className="carousel-div"
          id={`rotatingDiv_${index}`}
          zIndexBump={index === nextIndex}
        >
          <RotationText>{contentData.body}</RotationText>
          <LinkButton gradient={gradient} link={contentData.link.url}>
            {contentData.link.text}
          </LinkButton>
          <RotatingIllustrationDiv>
            {contentData.img && (
              <RotatingIllustration
                id={`illustration_${index}`}
                alt={contentData.alt ?? `illustration_${index}`}
                image={contentData.img}
                loading="lazy"
                objectFit="contain"
                imgStyle={{height: "90%"}}
              />
            )}
          </RotatingIllustrationDiv>
        </RotationDiv>
      )
    })
  }, [sections, gradient, nextIndex])

  return (
    <OuterWrapper className={className}>
      <InnerWrapper
        setWidth={useMedia("1440px", "86.747vw", "94.141vw", undefined)}
      >
        <Wrapper>
          <ButtonBG>
            <ButtonRow>{allButtons}</ButtonRow>
          </ButtonBG>
          <CarouselBG>
            <TextDiv>
              <Callout
                variant="lightmode"
                title={{
                  content: t(titleMain),
                  size: useMedia(text.h3, text.h3, text.h3, text.h1Mobile),
                  width: titleWidth,
                }}
                text={{
                  content: t(descMain),
                  width: textWidth,
                }}
                flex={useMedia(false, false, true, false)}
                buttons={[
                  {
                    text: t("Contact Sales") ?? "Contact Sales",
                    arrow: true,
                    route: checkedLocaleLink("/contact-us", language),
                  },
                ]}
              />
              {tablet && <Divider />}
            </TextDiv>
            <RotatingHolder>{rotatingContent}</RotatingHolder>
          </CarouselBG>
        </Wrapper>
      </InnerWrapper>
    </OuterWrapper>
  )
}

const Buttons = styled.div`
  ${text.bodyS};
  height: auto;
  width: auto;
  cursor: pointer;
  background-color: transparent;

  ${fresponsive(css`
    border-radius: 40px;
    padding: 4px 15px 5px;
  `)}

  ${media.hover} {
    :hover {
      background-color: white !important;
      color: ${colors.blue800} !important;
    }
  }
`

const RotationDiv = styled.div<{ zIndexBump: boolean }>`
  opacity: 0;
  flex-direction: column;
  opacity: 0;
  position: absolute;
  top: 0;
  z-index: ${props => (props.zIndexBump ? 2 : "initial")};

  ${fresponsive(css`
    width: 570px;
    height: 632px;
  `)}

  ${ftablet(css`
    width: 352px;
    height: 390px;
  `)}


  :first-child {
    opacity: 1;
    position: relative;
  }
`

const RotationText = styled.p`
  ${text.bodyR};
  color: ${colors.blue800};

  ${fresponsive(css`
    margin-bottom: 20px;
    width: 548px;
  `)}

  ${ftablet(css`
    margin-bottom: 20px;
    width: 600px;
  `)}
`

const RotatingIllustrationDiv = styled.div`
  ${fresponsive(css`
    width: 700px;
    height: 550px;
  `)}

  ${ftablet(css`
    width: 700px;
    height: 550px;
    margin-left: 92px;
  `)}
`

const RotatingIllustration = styled(GatsbyImage)<{ objectFit: string , imgStyle?: any }>`
  position: relative;
  max-width: unset;

  ${fresponsive(css`
    width: 700px;
    height: 550px;
    margin-top: 20px;
  `)}
`

const OuterWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  background-color: ${colors.lightMode};

  ${fresponsive(css`
    padding-top: 180px;
    padding-bottom: 231px;
  `)}

  ${ftablet(css`
    padding-top: 150px;
    padding-bottom: 250px;
  `)}

  ${fmobile(css`
    display: none;
  `)}
`

const Wrapper = styled.div`
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  ${fresponsive(css`
    width: 1440px;
    height: 751px;
  `)}

  ${ftablet(css`
    width: 100%;
    height: 1024px;
  `)}
`

const ButtonBG = styled.div`
  width: fit-content;
  margin: 0 auto;

  background-color: ${colors.blue10};

  ${fresponsive(css`
    max-width: 1361.2px;
    height: 70px;
    padding: 20px 60px;
    border-top-left-radius: 20px;
    border-top-right-radius: 20px;
  `)}

  ${ftablet(css`
    max-width: 964px;
    height: 60px;
    padding: 15px 30px;
    border-top-left-radius: 20px;
    border-top-right-radius: 20px;
  `)}
`

const ButtonRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  padding: 0;
  width: auto;

  ${fresponsive(css`
    gap: 12px;
    max-width: 1361px;
    height: 30px;
  `)}

  ${ftablet(css`
    white-space: nowrap;
    gap: 1vw;
    max-width: 964px;
    height: 30px;
  `)}

  ${Buttons}:nth-child(1) {
    background-color: ${colors.blue400};
  }
`

const CarouselBG = styled.div`
  display: flex;
  flex-direction: row;
  background-color: white;

  ${fresponsive(css`
    gap: 60px;
    width: 1440px;
    height: 680px;
    border-radius: 40px;
    padding-left: 120px;
    padding-top: 100px;
  `)}

  ${ftablet(css`
    flex-direction: column;
    gap: unset;
    width: 100%;
    height: 960px;
    border-radius: 40px;
    padding-left: 65px;
    padding-top: 80px;
  `)}
`

const TextDiv = styled.div`
  ${fresponsive(css`
    width: 571px;
    height: 371px;
  `)}

  ${ftablet(css`
    width: 834px;
    height: auto;
    margin-bottom: 40px;
  `)}
`

const Divider = styled.div`
  ${ftablet(css`
    height: 1px;
    width: 834px;
    background-color: ${colors.blue10};
    margin-top: 40px;
    margin-bottom: 20px;
  `)}
`

const RotatingHolder = styled.div`
  position: relative;
  ${media.tablet} {
    display: flex;
    flex-direction: column;
  }
`
